import AcfFeaturesList from "@/Components/Front/Blocks/Acf/AcfFeaturesList";
import WebpImage from "@/Components/Front/Blocks/WebpImage";
import FrontText from "@/Components/Front/FrontText";
import FrontDotsDecoration from "@/Components/Front/Partials/FrontDotsDecoration";
import FrontSeoMetaTags from "@/Components/Front/Partials/FrontSeoMetaTags";
import Column from "@/Components/Grid/Column";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import FrontLayout from "@/Layouts/System/FrontLayout";
import { Icon } from "@iconify/react";
import { useTranslation, Trans } from "react-i18next";

const AboutUs = ({ seo }) => {
    const { t, i18n } = useTranslation();

    return (
        <FrontLayout
            breadcrumbs={[{ title: t("about_us.breadcrumbs"), current: true }]}
        >
            <FrontSeoMetaTags seo={seo} />
            <section>
                <Container>
                    <Row>
                        <Column>
                            <FrontText
                                className="my-10 xl:mb-0 font-domine font-bold text-dark-blue-standard"
                                textSize={"38/43"}
                            >
                                <h1>{t("about_us.breadcrumbs")}</h1>
                            </FrontText>
                        </Column>
                    </Row>
                    <Row
                        className={`xl:mb-c120 lg:mb-20 md:mb-15 mb-10 justify-between items-center xl:gap-x-0 lg:gap-x-4 lg:flex-nowrap gap-y-10`}
                    >
                        <Column xl={6} lg={6}>
                            <FrontText className="mb-5">
                                <strong>{t("about_us.entry_bold_text")}</strong>
                            </FrontText>
                            <FrontText>
                                <p>
                                    <Trans
                                        i18nKey={"about_us.second_entry_text"}
                                    />
                                </p>
                            </FrontText>
                        </Column>

                        <Column xl={5} lg={6}>
                            <WebpImage
                                className="w-full h-auto rounded-10"
                                sources={[
                                    {
                                        width: 768,
                                        path: `/storage/images/DSC00004f-size-767`,
                                        mime: "jpg",
                                    },
                                    {
                                        width: 1,
                                        path: `/storage/images/DSC00004f-size-530`,
                                        mime: "jpg",
                                    },
                                ]}
                                alt="Michał Chodecki"
                                fallback={"/storage/images/DSC00004f"}
                                seo={{
                                    width: 530,
                                    height: 354,
                                }}
                            />
                        </Column>
                    </Row>
                    <Row>
                        <Column>
                            <FrontText
                                className="font-domine mb-11 font-bold text-dark-blue-standard"
                                textSize={"38/43"}
                            >
                                <h2>{t("about_us.h2")}</h2>
                            </FrontText>
                        </Column>
                    </Row>
                    <Row>
                        <AcfFeaturesList
                            blockSpacing="xl:mb-c120 lg:mb-20 mb-15"
                            list={t("about_us.whyUsContent", {
                                returnObjects: true,
                            })}
                        />
                    </Row>
                    <Row>
                        <Column>
                            <FrontText
                                className="font-domine mb-10 font-bold text-dark-blue-standard"
                                textSize={"38/43"}
                            >
                                <h4>{t("about_us.h4")}</h4>
                            </FrontText>
                        </Column>
                    </Row>
                </Container>
            </section>
            <section className="bg-new-blue lg:py-20 md:py-16 py-10 xl:mb-c120 lg:mb-20 md:mb-15 mb-10 overflow-hidden">
                <Container>
                    <Row className="justify-between">
                        <Column xl={6} lg={6} md={6}>
                            <FrontText className="text-white font-semibold mb-5">
                                <p>{t("about_us.savillsPolandHeading")}</p>
                            </FrontText>
                            <FrontText className="text-white mb-5 [&_p]:mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-4">
                                <Trans i18nKey={`about_us.savillsPolandText`} />
                            </FrontText>
                        </Column>

                        <Column
                            xl={5}
                            lg={5}
                            md={6}
                            className={`relative md:mt-0 mt-10`}
                        >
                            <WebpImage
                                className="rounded-10 w-full h-auto"
                                sources={[
                                    {
                                        width: 768,
                                        path: `/storage/images/savills-poland2-size-1300`,
                                        mime: "jpg",
                                    },
                                    {
                                        width: 1,
                                        path: `/storage/images/savills-poland2-size-767`,
                                        mime: "jpg",
                                    },
                                ]}
                                alt={"savills-poland"}
                                fallback={`/storage/images/savills-poland2.jpg`}
                                seo={{
                                    width: 520,
                                    height: 346,
                                }}
                            />
                            <FrontDotsDecoration className="absolute -top-12 -right-10" />
                        </Column>
                    </Row>
                </Container>
            </section>
            <section className="lg:mb-20 md:mb-10">
                <Container>
                    <Row>
                        <Column>
                            <FrontText
                                textSize="32/36-m-28/32"
                                className="font-bold font-domine text-center lg:mb-15 mb-10 text-heading-blue"
                            >
                                <h5>{t("about_us.h5")}</h5>
                            </FrontText>
                        </Column>

                        <Column className="flex sm:justify-evenly items-center sm:flex-row flex-col gap-y-5">
                            <div className="sm:w-44 w-64 h-[11.125rem] border-yellow border-4 p-4 flex gap-y-5 flex-col justify-between">
                                <FrontText
                                    textSize="16/20"
                                    className="font-semibold text-standard-black uppercase text-center"
                                >
                                    {t("about_us.statisticsBoxFirstHeading")}
                                </FrontText>

                                <FrontText
                                    className="h-[58px] font-bold text-dark-blue-standard text-center"
                                    textSize="24/29"
                                >
                                    <Trans
                                        i18nKey={
                                            "about_us.statisticsBoxFirstDescription"
                                        }
                                    />
                                </FrontText>

                                <FrontText
                                    textSize="16/20"
                                    className="font-semibold text-standard-black text-center"
                                >
                                    {t("about_us.statisticsBoxFirstText")}
                                </FrontText>
                            </div>

                            <div className="sm:w-44 w-64 h-[11.125rem] border-yellow border-4 p-4 flex gap-y-5 flex-col justify-between">
                                <FrontText
                                    textSize="16/20"
                                    className="font-semibold text-standard-black uppercase text-center"
                                >
                                    {t("about_us.statisticsBoxSecondHeading")}
                                </FrontText>

                                <FrontText
                                    className="flex justify-center items-center font-bold text-dark-blue-standard text-center"
                                    textSize="24/29"
                                >
                                    {t(
                                        "about_us.statisticsBoxSecondDescription",
                                    )}
                                </FrontText>

                                <FrontText
                                    textSize="16/20"
                                    className="font-semibold text-standard-black text-center"
                                >
                                    {t("about_us.statisticsBoxSecondText")}
                                </FrontText>
                            </div>

                            <div className="sm:w-44 w-64 h-[11.125rem] border-yellow border-4 px-[11px] py-4 flex gap-y-5 md:gap-y-[18px] flex-col justify-between">
                                <FrontText
                                    textSize="16/20"
                                    className="font-semibold text-standard-black uppercase text-center flex"
                                >
                                    <div className="mx-auto">
                                        <p className="table-caption">
                                            {t(
                                                "about_us.statisticsBoxThirdHeading",
                                            )}
                                        </p>
                                    </div>
                                </FrontText>

                                <a
                                    href="https://ir.savills.com"
                                    target="_blank"
                                    title="savills.com"
                                    className="bg-yellow text-standard-black hover:bg-30548A hover:text-white transition-all ease-in-out duration-200 rounded-4 py-c10 flex gap-c10 text-center justify-center items-center"
                                >
                                    <FrontText
                                        textSize="14/20"
                                        className="font-bold"
                                    >
                                        <span>ir.savills.com</span>
                                    </FrontText>
                                    <Icon
                                        icon="la:mouse-pointer"
                                        width={20}
                                        height={20}
                                    />
                                </a>
                            </div>
                        </Column>
                    </Row>
                </Container>
            </section>
            {/* <section>
                <Container>
                    <Row>
                        <Column>
                            <FrontText className="font-domine mb-20 font-bold text-dark-blue-standard" textSize={'38/43'}>
                                <h5>{t('about_us.h5')}</h5>
                            </FrontText>
                        </Column>
                    </Row>
                    <Row>
                        <Column xl={6} lg={6} md={6}>
                            <WebpImage
                                lazy
                                sources={[
                                    {
                                        width: 768,
                                        path: '/storage/images/savills-worldwide-size-615',
                                        mime: 'jpg',
                                    },
                                    {
                                        width: 1,
                                        path: '/storage/images/savills-worldwide',
                                        mime: 'jpg'
                                    }
                                ]}
                                alt={'savills-worldwide'}
                                className="md:w-11/12 w-full h-auto"
                                fallback={`/storage/images/savills-worldwide.jpg`}
                                seo={
                                    {
                                        width: 570,
                                        height: 280
                                    }
                                }
                            />
                        </Column>
                        <Column xl={6} lg={6} md={6}>
                            <FrontText className="mb-5 font-semibold">
                                <p>{t('about_us.savillsWorldwideHeading')}</p>
                            </FrontText>
                            <FrontText className="[&_p]:mb-5">
                                <Trans i18nKey={`about_us.savillsWorldwideText`} />
                            </FrontText>
                            <Link title={t('about_us.contact')} href={resolveLocaleRoute('front.contact')} className="max-w-[177px] w-full h-10 px-[18px] py-2.5 border-30548A border-[1px] bg-30548A rounded-lg justify-center items-center gap-2.5 inline-flex text-white uppercase mt-10 font-semibold text-13 transition-all ease-in-out duration-150 hover:bg-white hover:text-black mb-40">
                                {t('about_us.contact')}
                            </Link>
                        </Column>
                    </Row>
                </Container>
            </section> */}
        </FrontLayout>
    );
};

export default AboutUs;
